import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthGuard from '@/components/feature/AuthGuard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';
import SevenFieldForm from './components/SevenFieldForm';
import { usePracticeSupabase, type SevenFields } from './hooks/usePracticeSupabase';

// ── Login / Signup Component ───────────────────────

function AuthForm() {
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);

  return (
    <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8 max-w-md mx-auto space-y-6 text-center">
      <AngelIcon variant="gold" size="md" />
      <div>
        <h2 className="text-xl text-foreground-800 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
          登入 AUM1001
        </h2>
        <p className="text-xs text-foreground-500 leading-relaxed">
          AI Angel Practice 使用全站統一的 AUM1001 帳號，與 RG、OG、宇炁療育、ADM 管理共用同一組登入憑證。
        </p>
      </div>
      <Link
        to={`/account?returnTo=${returnTo}`}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer w-full"
      >
        <i className="ri-login-box-line text-lg" />
        前往 /account 登入
      </Link>
      <p className="text-xs text-foreground-400">
        尚未建立 AUM1001 帳號？請至 /account 完成註冊與 Email 驗證。
      </p>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────

export default function PortalPractice() {
  return (
    <AuthGuard>
      {(auth) => (
        <PortalPracticeInner
          user={auth.user}
          sessionExpired={auth.sessionExpired}
          signOut={auth.signOut}
          authError={auth.error}
        />
      )}
    </AuthGuard>
  );
}

function PortalPracticeInner({
  user,
  sessionExpired,
  signOut,
  authError,
}: {
  user: { id: string; email?: string } | null;
  sessionExpired: boolean;
  signOut: () => Promise<void>;
  authError: string | null;
}) {
  const {
    pageState,
    draft,
    submittedEvent,
    entitlement,
    errorMessage,
    saveDraft,
    giveConsent,
    submitF1,
    revokeConsent,
    resetToConsent,
    loadData,
  } = usePracticeSupabase(user);

  const [currentFields, setCurrentFields] = useState<SevenFields>(() => draft || {
    event_description: '',
    body_sensation: '',
    emotions: [],
    emotion_free_input: '',
    meaning: '',
    automatic_response: '',
    new_response: '',
    result_gratitude: '',
  });

  // `draft` is loaded asynchronously from Supabase. Keep the form in sync
  // after the remote draft arrives (including refresh, relogin, or a new device).
  useEffect(() => {
    setCurrentFields(draft);
  }, [draft]);

  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventDescriptionLength = currentFields.event_description.trim().length;
  const eventDescriptionIsValid = eventDescriptionLength >= 50 && eventDescriptionLength <= 600;

  const handleFieldsChange = useCallback((fields: SevenFields) => {
    setCurrentFields(fields);
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      saveDraft(fields);
    }, 1500);
  }, [saveDraft]);

  const handleConsent = useCallback(() => {
    giveConsent();
  }, [giveConsent]);

  const handleSubmit = useCallback(() => {
    submitF1(currentFields);
  }, [submitF1, currentFields]);

  // ── Render states ──

  const renderContent = () => {
    // Session expired
    if (sessionExpired) {
      return (
        <div className="text-center py-12 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
            <i className="ri-timer-line text-amber-500 text-2xl" />
          </div>
          <h2 className="text-xl text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>登入階段已過期</h2>
          <p className="text-sm text-foreground-500">請重新登入以繼續。</p>
          <button onClick={signOut} className="whitespace-nowrap px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer">重新登入</button>
        </div>
      );
    }

    // Auth error
    if (authError) {
      return (
        <div className="text-center py-12 space-y-4">
          <p className="text-sm text-rose-600">{authError}</p>
          <button onClick={signOut} className="whitespace-nowrap px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer">重新開始</button>
        </div>
      );
    }

    // Loading
    if (pageState === 'LOADING') {
      return (
        <div className="text-center py-16 space-y-3">
          <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">載入你的練習資料……</p>
        </div>
      );
    }

    // Unauthenticated
    if (pageState === 'UNAUTHENTICATED') {
      return <AuthForm />;
    }

    // Error state (with retry)
    if (pageState === 'ERROR') {
      return (
        <div className="text-center py-12 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-rose-500 text-2xl" />
          </div>
          <p className="text-sm text-rose-700 max-w-sm mx-auto">{errorMessage || '發生未知錯誤'}</p>
          <button onClick={loadData} className="whitespace-nowrap px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer">重試</button>
        </div>
      );
    }

    // Consent required
    if (pageState === 'CONSENT_REQUIRED') {
      return (
        <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8 space-y-6">
          <div className="text-center">
            <AngelIcon variant="gold" size="lg" />
            <h2 className="text-xl text-foreground-800 mt-4 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              歡迎來到 AI Angel Practice
            </h2>
            <p className="text-sm text-foreground-500 max-w-md mx-auto leading-relaxed">
              你將完成三次免費的生命練習。AI Angel 會陪你記錄七項短紀錄，並在練習後留下一個可驗證的上升點。
            </p>
          </div>

          <div className="bg-foreground-50/50 rounded-lg p-4 space-y-2 text-sm text-foreground-600">
            <p className="font-medium text-foreground-700">開始之前，請了解：</p>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <i className="ri-information-line text-accent-400 mt-0.5 shrink-0" />
                <span>AI Angel 不是人類導師，它是你的練習陪伴者。</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-information-line text-accent-400 mt-0.5 shrink-0" />
                <span>你的練習紀錄會安全儲存在伺服器中，只有你能讀取。</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-information-line text-accent-400 mt-0.5 shrink-0" />
                <span>你有三次免費練習。第四次需要選擇付費方案。</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-information-line text-accent-400 mt-0.5 shrink-0" />
                <span>AI Angel 不診斷疾病、不取代專業醫療或心理協助。</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-information-line text-accent-400 mt-0.5 shrink-0" />
                <span>你可以隨時撤回同意，撤回後將無法繼續練習。</span>
              </li>
            </ul>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleConsent}
              className="whitespace-nowrap px-8 py-3 rounded-md bg-accent-500 text-background-50 text-sm md:text-base hover:bg-accent-600 transition-colors cursor-pointer"
            >
              我理解，開始第一次練習
            </button>
          </div>
        </div>
      );
    }

    // Entitlement denied
    if (pageState === 'ENTITLEMENT_DENIED') {
      return (
        <div className="text-center py-12 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-lock-line text-foreground-400 text-2xl" />
          </div>
          <h2 className="text-xl text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
            免費練習次數已用完
          </h2>
          <p className="text-sm text-foreground-500 max-w-sm mx-auto">
            你已經完成了 {entitlement?.free_uses_total || 3} 次免費練習。請等待後續付費方案開放。
          </p>
          <Link
            to="/free-angel"
            className="whitespace-nowrap inline-flex px-6 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
          >
            回到免費陪伴
          </Link>
        </div>
      );
    }

    // Risk paused
    if (pageState === 'RISK_PAUSED') {
      return (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
              <i className="ri-alert-line text-rose-500 text-xl" />
            </div>
            <div>
              <h3 className="text-base font-medium text-rose-800 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                練習已暫停
              </h3>
              <p className="text-sm text-rose-700 leading-relaxed">
                你的練習已被暫停。如有疑問請聯絡支援團隊。AI Angel 的設計，是在這時候停下來——不是離開你，而是確保你得到真正需要的支持。
              </p>
            </div>
          </div>
          <Link
            to="/free-angel"
            className="whitespace-nowrap inline-flex px-6 py-2.5 rounded-md bg-rose-500 text-white text-sm hover:bg-rose-600 transition-colors cursor-pointer"
          >
            返回免費陪伴
          </Link>
        </div>
      );
    }

    // F1 draft - show form
    if (pageState === 'F1_DRAFT') {
      return (
        <div className="space-y-6">
          {entitlement && (
            <PracticeStatusBar
              freeConsumed={entitlement.free_uses_consumed}
              freeTotal={entitlement.free_uses_total}
              remaining={entitlement.free_uses_remaining}
            />
          )}

          <SevenFieldForm
            fields={currentFields}
            onChange={handleFieldsChange}
            sequenceNo={1}
          />

          <div className="flex justify-center pt-2">
            <button
              onClick={handleSubmit}
              disabled={!eventDescriptionIsValid || !currentFields.body_sensation}
              className={`whitespace-nowrap px-8 py-3 rounded-md text-sm md:text-base transition-colors cursor-pointer ${
                eventDescriptionIsValid && currentFields.body_sensation
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-foreground-200 text-foreground-400 cursor-not-allowed'
              }`}
            >
              送出練習，儲存到伺服器
            </button>
          </div>

          <div className="text-center pt-4">
            <button
              onClick={() => { revokeConsent(); }}
              className="whitespace-nowrap text-xs text-foreground-400 hover:text-rose-500 underline cursor-pointer transition-colors"
            >
              撤回同意（將無法繼續練習）
            </button>
          </div>
        </div>
      );
    }

    // Submitting
    if (pageState === 'SUBMITTING') {
      return (
        <div className="text-center py-16 space-y-4">
          <div className="w-12 h-12 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-600">正在儲存你的練習紀錄……</p>
          <p className="text-xs text-foreground-400">這只需要幾秒鐘，請勿關閉頁面。</p>
        </div>
      );
    }

    // F1 submitted - show result
    if (pageState === 'F1_SUBMITTED' && submittedEvent) {
      return (
        <div className="space-y-8">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <i className="ri-check-double-line text-emerald-500 text-3xl" />
            </div>
            <h2 className="text-2xl text-foreground-800 mt-4 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              練習已完成
            </h2>
            <p className="text-sm text-foreground-500 max-w-sm mx-auto leading-relaxed">
              你的第一次練習已安全儲存到伺服器。登出後重新登入，這份紀錄仍會完整保留。
            </p>
          </div>

          {/* Submitted data summary */}
          <div className="bg-background-100 border border-foreground-200/40 rounded-lg p-5 space-y-3">
            <p className="text-xs uppercase tracking-wider text-foreground-400">已儲存的練習紀錄</p>
            <div className="space-y-2">
              <FieldSummary label="① 事件" value={submittedEvent.event_description} />
              <FieldSummary label="② 身體感覺" value={submittedEvent.body_sensation} />
              <FieldSummary label="③ 情緒" value={
                [...(submittedEvent.emotions || []), submittedEvent.emotion_free_input || ''].filter(Boolean).join('、') || '(未填寫)'
              } />
              <FieldSummary label="④ 理解" value={submittedEvent.meaning} />
              <FieldSummary label="⑤ 自動反應" value={submittedEvent.automatic_response} />
              <FieldSummary label="⑥ 新回應" value={submittedEvent.new_response} />
              <FieldSummary label="⑦ 結果與感恩" value={submittedEvent.result_gratitude} />
            </div>
            <p className="text-[10px] text-foreground-400">
              送出時間：{submittedEvent.confirmed_at ? new Date(submittedEvent.confirmed_at).toLocaleString('zh-TW') : '剛'}
            </p>
          </div>

          {/* Entitlement info */}
          {entitlement && (
            <div className="text-center space-y-2">
              <PracticeStatusBar
                freeConsumed={entitlement.free_uses_consumed}
                freeTotal={entitlement.free_uses_total}
                remaining={entitlement.free_uses_remaining}
              />
              <p className="text-xs text-foreground-400">
                你還有 {entitlement.free_uses_remaining} 次免費練習。AI Angel 會在 Version 31 提供完整的 AI 回應。
              </p>
            </div>
          )}

          <div className="flex items-center justify-center gap-3 pt-4 flex-wrap">
            <Link
              to="/free-angel"
              className="whitespace-nowrap inline-flex px-6 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
            >
              回到免費陪伴
            </Link>
            <button
              onClick={signOut}
              className="whitespace-nowrap px-4 py-2.5 rounded-md border border-foreground-200 text-xs text-foreground-400 hover:bg-foreground-50 transition-colors cursor-pointer"
            >
              登出
            </button>
          </div>

          {/* Test: Resubmit button for idempotency verification */}
          <details className="mt-6 bg-foreground-50/30 border border-foreground-100/40 rounded-lg p-4">
            <summary className="text-[10px] text-foreground-400 cursor-pointer uppercase tracking-wider">
              V30 驗收：測試重複送出（Idempotency）
            </summary>
            <div className="mt-3 space-y-3">
              <p className="text-xs text-foreground-500">
                按此按鈕以相同內容再次提交。若 idempotency 正確運作，系統應回傳「已存在」而不消耗額外次數。
              </p>
              <button
                onClick={() => {
                  submitF1({
                    event_description: submittedEvent.event_description,
                    body_sensation: submittedEvent.body_sensation,
                    emotions: submittedEvent.emotions || [],
                    emotion_free_input: submittedEvent.emotion_free_input || '',
                    meaning: submittedEvent.meaning,
                    automatic_response: submittedEvent.automatic_response,
                    new_response: submittedEvent.new_response,
                    result_gratitude: submittedEvent.result_gratitude,
                  });
                }}
                className="whitespace-nowrap px-4 py-2 rounded-md bg-amber-100 text-amber-700 text-xs hover:bg-amber-200 transition-colors cursor-pointer"
              >
                嘗試重複送出（不同 idempotency key，測試 F1_EXISTS）
              </button>
            </div>
          </details>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main className="pt-20 pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* ── Page Header ──────────────────────────── */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <AngelIcon variant="gold" size="md" />
            </div>
            <h1
              className="text-2xl md:text-3xl text-foreground-800 mb-1"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              AI Angel Practice
            </h1>
            <p className="text-xs text-foreground-400 uppercase tracking-[0.15em] mb-2">
              F1 練習 &middot; Version 30 &middot; Real Supabase
            </p>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto leading-relaxed">
              記錄一次真實的生命事件。AI Angel 從你的七項短紀錄中，辨識你已經走過的路——從自動反應，走向有意識的選擇。
            </p>

            {/* Logged-in user indicator */}
            {user && (
              <div className="flex items-center justify-center gap-2 mt-3">
                <span className="text-[10px] text-foreground-400 bg-foreground-100 px-2 py-1 rounded-full">
                  {user.email || '已登入'}
                </span>
                <button
                  onClick={signOut}
                  className="whitespace-nowrap text-[10px] text-foreground-400 hover:text-rose-500 underline cursor-pointer transition-colors"
                >
                  登出
                </button>
              </div>
            )}
          </div>

          {renderContent()}

          {/* ── Debug (development only) ─────────────── */}
          <details className="mt-12 bg-foreground-50/30 border border-foreground-100/40 rounded-lg p-4">
            <summary className="text-[10px] text-foreground-400 cursor-pointer uppercase tracking-wider">
              Dev: State Debug
            </summary>
            <div className="mt-2 text-[10px] text-foreground-400 font-mono space-y-1">
              <p>Page State: {pageState}</p>
              <p>User: {user?.id ? user.id.slice(0, 8) + '...' : 'null'}</p>
              <p>Entitlement: {entitlement ? `${entitlement.free_uses_consumed}/${entitlement.free_uses_total} consumed, ${entitlement.free_uses_remaining} remaining` : 'null'}</p>
              <p>Submitted: {submittedEvent ? `Yes (id: ${submittedEvent.id})` : 'No'}</p>
              <p>Auth Error: {authError || 'None'}</p>
              <p>Page Error: {errorMessage || 'None'}</p>
              <button
                onClick={resetToConsent}
                className="mt-2 whitespace-nowrap px-3 py-1 rounded bg-amber-100 text-amber-600 text-[10px] hover:bg-amber-200 transition-colors cursor-pointer"
              >
                Reset to Consent
              </button>
            </div>
          </details>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ── Sub-components ─────────────────────────────────

function PracticeStatusBar({ freeConsumed, freeTotal, remaining }: {
  freeConsumed: number;
  freeTotal: number;
  remaining: number;
}) {
  return (
    <div className="flex items-center justify-between bg-background-100 border border-foreground-200/40 rounded-lg px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="text-sm text-foreground-600">練習次數</span>
        <div className="flex gap-1">
          {Array.from({ length: freeTotal }, (_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full ${
                i < freeConsumed ? 'bg-accent-400' : 'bg-foreground-200'
              }`}
            />
          ))}
        </div>
      </div>
      <span className="text-xs text-foreground-400">
        剩餘 {remaining} 次免費練習
      </span>
    </div>
  );
}

function FieldSummary({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm">
      <span className="text-foreground-400 font-medium">{label}：</span>
      <span className="text-foreground-700">
        {value.length > 120 ? value.slice(0, 120) + '...' : value}
      </span>
    </div>
  );
}
